import { getAnalytics, isSupported, logEvent, setUserId, setUserProperties } from "firebase/analytics";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import app, { auth, db } from "./firebase";
import { BUILD_INFO } from "../generatedBuildInfo";

const ALLOWED_EVENT_NAMES = new Set([
  "account_created",
  "onboarding_started",
  "onboarding_completed",
  "calculator_opened",
  "calculation_completed",
  "calculation_saved",
  "calculator_pinned",
  "calculator_unpinned",
  "dashboard_opened",
  "personal_project_created",
  "personal_project_updated",
  "onboarding_preferences_saved",
  "recommended_calculator_opened",
  "manufacturing_plan_created",
  "sop_created",
  "setup_sheet_created",
  "job_traveler_created",
  "ai_assistant_opened",
  "ai_request_completed",
  "ai_request_failed",
  "file_upload_succeeded",
  "file_upload_failed",
  "collaboration_invite_sent",
  "collaboration_invite_accepted",
  "upgrade_page_opened",
  "checkout_started",
  "checkout_completed",
  "checkout_canceled",
  "feedback_submitted",
  "feature_usage",
]);

let analyticsPromise;

const getClientAnalytics = () => {
  if (!analyticsPromise) {
    analyticsPromise = isSupported()
      .then((supported) => (supported ? getAnalytics(app) : null))
      .catch(() => null);
  }
  return analyticsPromise;
};

const cleanParams = (params = {}) =>
  Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => value !== undefined && value !== null && value !== "")
      .map(([key, value]) => [
        String(key).slice(0, 40),
        typeof value === "number" || typeof value === "boolean"
          ? value
          : String(value).slice(0, 120),
      ])
  );

export const identifyUser = async (user, traits = {}) => {
  const analytics = await getClientAnalytics();
  if (!analytics || !user?.uid) return;
  setUserId(analytics, user.uid);
  setUserProperties(analytics, cleanParams(traits));
};

export const trackEvent = async (eventName, params = {}) => {
  const safeName = ALLOWED_EVENT_NAMES.has(eventName) ? eventName : "feature_usage";
  const safeParams = {
    ...cleanParams(params),
    app_version: BUILD_INFO.version || "unknown",
  };
  const user = auth.currentUser;

  try {
    const analytics = await getClientAnalytics();
    if (analytics) logEvent(analytics, safeName, safeParams);
  } catch (_) {
    // Analytics should never block the application.
  }

  try {
    if (user?.uid) {
      await addDoc(collection(db, "analyticsEvents"), {
        eventName: safeName,
        params: safeParams,
        userId: user.uid,
        timestamp: serverTimestamp(),
        timestampIso: new Date().toISOString(),
      });
    }
  } catch (_) {
    // Firestore analytics is best-effort until production rules/indexes are deployed.
  }
};

export const trackPageView = (route = "") =>
  trackEvent("feature_usage", { feature: "page_view", route: String(route || "").slice(0, 120) });
